import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { workerApi } from '../services/workerApi';
import {
  RefreshCw, Facebook, Instagram, BarChart2,
} from 'lucide-react';
import { PageDashboard } from '../components/dashboard/PageDashboard';

// ── helpers ───────────────────────────────────────────────────────────────────

const platformColor: Record<string, string> = {
  facebook:  'bg-blue-100 text-blue-700',
  instagram: 'bg-pink-100 text-pink-700',
  zalo:      'bg-cyan-100 text-cyan-700',
};

const platformIcon = (type: string) => {
  if (type === 'facebook')  return <Facebook  size={16} className="inline mr-1" />;
  if (type === 'instagram') return <Instagram size={16} className="inline mr-1" />;
  return null;
};

function platformLabel(type: string) {
  return { facebook: 'Facebook', instagram: 'Instagram', zalo: 'Zalo' }[type] ?? type;
}

// ── main ──────────────────────────────────────────────────────────────────────

export const Dashboard: React.FC<{ platforms?: any[]; onRefresh?: () => void | Promise<any>; dataReady?: boolean }> = ({ platforms: propPlatforms, onRefresh, dataReady = false }) => {
  const { t } = useTranslation('dashboard');
  const [platforms, setPlatforms] = useState<any[]>(propPlatforms ?? []);
  const [filterPageId, setFilterPageId] = useState<string>('');
  const [platformsLoading, setPlatformsLoading] = useState(false);

  // Sync from parent prop
  useEffect(() => {
    if (propPlatforms && propPlatforms.length > 0) setPlatforms(propPlatforms);
  }, [propPlatforms]);

  const pageMap = useMemo(() => {
    const m: Record<string, { name: string; avatar: string }> = {};
    for (const p of platforms) {
      const pid = p.channel_id || p.platform_id || '';
      if (pid) m[pid] = {
        name:   p.name || pid,
        avatar: p.avatar || '',
      };
    }
    return m;
  }, [platforms]);

  const platformsByType = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const p of platforms) {
      const t = p.platform ?? 'other';
      if (!map[t]) map[t] = [];
      map[t].push(p);
    }
    return map;
  }, [platforms]);

  const loadPlatforms = useCallback(async () => {
    setPlatformsLoading(true);
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        const data: any = await workerApi.getPlatforms();
        setPlatforms(data?.data ?? []);
      }
    } catch { /* ignore */ }
    finally { setPlatformsLoading(false); }
  }, [onRefresh]);

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{t('title')}</h1>
          <p className="text-xs text-gray-400 mt-0.5">{t('subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filterPageId}
            onChange={(e) => setFilterPageId(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
            title="Lọc theo trang"
            aria-label={t('filterByPage')}
          >
            <option value="">{t('allPages')}</option>
            {platforms.map((p) => (
              <option key={p.id} value={p.channel_id}>{p.name}</option>
            ))}
          </select>
          <button
            onClick={loadPlatforms}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
          >
            <RefreshCw size={13} className={platformsLoading ? 'animate-spin' : ''} />
            {t('refresh')}
          </button>
        </div>
      </div>

      {/* Platform overview card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <BarChart2 size={15} className="text-blue-500" />
          {t('connectedPages')}
        </h2>
        {platforms.length === 0 ? (
          <p className="text-xs text-gray-400">{t('noPages')}</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {Object.entries(platformsByType).map(([type, pages]) => (
              <div
                key={type}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${platformColor[type] ?? 'bg-gray-100 text-gray-600'}`}
              >
                {platformIcon(type)}
                <span>{platformLabel(type)}</span>
                <span className="bg-white/70 rounded-full px-1.5 py-0.5 font-bold">{pages.length}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-600">
              {t('total')} <span className="font-bold">{platforms.length} {t('pageSuffix')}</span>
            </div>
          </div>
        )}
        {platforms.length > 0 && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {platforms.map((p) => (
              <div key={p.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-xs">
                {p.avatar ? (
                  <img src={p.avatar} alt="" className="w-7 h-7 rounded-full object-cover border border-gray-200" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                    {(p.name ?? '?')[0].toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-medium text-gray-700 truncate">{p.name}</p>
                  <p className="text-gray-400 truncate">{p.channel_id}</p>
                </div>
                <span className={`ml-auto shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${p.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {p.status === 'active' ? 'Active' : p.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full dashboard (tabs: Thống kê / Ưu tiên / Khách hàng / Leads) */}
      <PageDashboard pageId={filterPageId || undefined} pageMap={pageMap} ready={dataReady} />
    </div>
  );
};

